import { BaseApiClient } from './base-client';
import { OrderCreateData, OrderResponse, PaymentIntent, PriceCalculation, VATCalculation } from '../types/orders';

export class OrdersApiClient extends BaseApiClient {
  async listOrders(): Promise<OrderResponse[]> {
    return this.get<OrderResponse[]>('/api/v1/orders/');
  }

  async calculateVAT(
    amountInCents: number, 
    country: string, 
    isCompany: boolean = false, 
    vatNumber?: string
  ): Promise<VATCalculation> {
    return this.post<VATCalculation>('/api/v1/orders/calculate-vat', {
      amount: amountInCents,  // Already in cents
      country,
      is_company: isCompany,
      vat_number: vatNumber
    });
  }

  async calculatePrice(
    data: OrderCreateData, 
    country: string, 
    isBusiness: boolean = false, 
    orderId?: number
  ): Promise<PriceCalculation> {
    // Create selected_options array with disk_space and users
    const selectedOptions = [
      { type: 'disk_space', value: data.disk_space },
      { type: 'users', value: data.users_count }
    ];
    
    // Add any additional options
    if (data.selected_options) {
      // Filter out any duplicate disk_space or users options
      const additionalOptions = data.selected_options.filter(
        opt => opt.type !== 'disk_space' && opt.type !== 'users'
      );
      selectedOptions.push(...additionalOptions);
    }

    return this.post<PriceCalculation>('/api/v1/orders/calculate-price', {
      product_id: data.product_id,
      disk_space: data.disk_space,
      users_count: data.users_count,
      selected_options: selectedOptions,
      period_id: data.period_id,
      order_id: orderId
    }, {
      params: {
        country,
        is_business: isBusiness
      }
    });
  }

  async createOrder(data: OrderCreateData): Promise<OrderResponse> {
    return this.post<OrderResponse>('/api/v1/orders/', data);
  }

  async checkOrderStatus(orderId: number): Promise<OrderResponse> {
    return this.get<OrderResponse>(`/api/v1/orders/${orderId}`);
  }

  async processOrder(orderId: number): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/orders/${orderId}/process`);
  }

  async extendOrder(serviceId: number, data: Partial<OrderCreateData>): Promise<OrderResponse> {
    return this.post<OrderResponse>(`/api/v1/orders/service/${serviceId}/extend`, data);
  }

  async retryPayment(orderId: number): Promise<OrderResponse> {
    return this.post<OrderResponse>(`/api/v1/orders/${orderId}/retry-payment`);
  }

  async getPaymentIntent(orderId: number): Promise<PaymentIntent> {
    return this.get<PaymentIntent>(`/api/v1/orders/${orderId}/payment-intent`);
  }

  // Helper method to check if an order is in a final state
  isOrderComplete(order: OrderResponse): boolean {
    const finalStates = ['PAID', 'FAILED', 'CANCELLED'];
    return finalStates.includes(order.status);
  }

  // Helper method to check if an order needs payment
  needsPayment(order: OrderResponse): boolean {
    return order.status === 'AWAITING_PAYMENT';
  }

  // Helper method to check if an order is successful
  isOrderSuccessful(order: OrderResponse): boolean {
    return order.status === 'PAID';
  }

  // Helper method to check if an order has failed
  hasOrderFailed(order: OrderResponse): boolean {
    return order.status === 'FAILED';
  }

  // Helper method to get formatted error message
  getOrderErrorMessage(order: OrderResponse): string | null {
    if (!this.hasOrderFailed(order)) {
      return null;
    }

    let message = order.error_message || 'Order processing failed';
    if (order.error_details) {
      try {
        const details = JSON.parse(order.error_details);
        if (typeof details === 'string') {
          message += `: ${details}`;
        } else if (details.message) {
          message += `: ${details.message}`;
        }
      } catch (e) {
        // If JSON parsing fails, use the raw error_details
        message += `: ${order.error_details}`;
      }
    }
    return message;
  }
}
